import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { useAuth } from "../lib/auth";
import {
  Menu,
  Gem,
  Sparkles,
  Coins,
  Gift,
  Package,
  Shield,
  LogOut,
  LogIn,
  MessageCircle,
  ChevronRight,
  Wallet,
} from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const CATEGORY_LINKS = [
  { to: "/", end: true, label: "Diamantes", sub: "Free Fire", Icon: Gem, color: "#ff4500", testid: "drawer-nav-diamonds" },
  { to: "/evolutivos", label: "Evolutivos", sub: "Tokens & Cajas", Icon: Sparkles, color: "#ff8a00", testid: "drawer-nav-evolutivos" },
  { to: "/robux", label: "Robux", sub: "Roblox", Icon: Coins, color: "#22c55e", testid: "drawer-nav-robux" },
  { to: "/recompensas", label: "Recompensas", sub: "Cajas sorpresa", Icon: Gift, color: "#fbbf24", testid: "drawer-nav-recompensas" },
];

export default function SideDrawer() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="open-drawer-btn"
          aria-label="Abrir menú"
          className="w-9 h-9 rounded-md grid place-items-center hover:bg-white/10 text-white"
        >
          <Menu className="w-5 h-5" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        data-testid="side-drawer"
        className="bg-[#0a0a0a] border-r border-[#ff4500]/30 text-white w-[86vw] sm:w-[360px] p-0 flex flex-col"
      >
        <SheetHeader className="p-5 pb-3 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#ff4500] diamond-glow">
              <img src="/logo.jpeg" alt="RECARGAS al Instante" className="w-full h-full object-cover" />
            </div>
            <div className="text-left leading-none">
              <SheetTitle className="font-display text-xl tracking-wider text-white">RECARGAS</SheetTitle>
              <div className="text-[10px] tracking-[0.3em] text-[#ff4500] mt-0.5">AL INSTANTE</div>
            </div>
          </div>
          {user && (
            <div className="mt-4 flex items-center gap-3 rounded-lg bg-white/5 p-2.5">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#ff4500]/20 grid place-items-center text-xs text-[#ff4500] font-bold">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1 text-left">
                <div className="text-sm text-white truncate">{user.name || user.email}</div>
                <div className="text-[11px] text-neutral-500 truncate">{user.email}</div>
              </div>
              {user.is_admin && <Shield className="w-4 h-4 text-[#ff4500] shrink-0" />}
            </div>
          )}
        </SheetHeader>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-3 py-2">Recargas</div>
          {CATEGORY_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={close}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 border ${isActive ? "" : "hover:bg-white/5"}`
              }
              style={({ isActive }) => ({
                background: isActive ? `${l.color}18` : "transparent",
                borderColor: isActive ? `${l.color}66` : "transparent",
                transition: "background 180ms ease, border-color 180ms ease",
              })}
            >
              <div
                className="w-9 h-9 rounded-md grid place-items-center shrink-0"
                style={{ background: "#0f0f0f", border: `1px solid ${l.color}66` }}
              >
                <l.Icon className="w-4 h-4" style={{ color: l.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white leading-tight">{l.label}</div>
                <div className="text-[10px] text-neutral-500 truncate">{l.sub}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-600" />
            </NavLink>
          ))}

          {user && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-3 py-2 mt-3">Mi cuenta</div>
              <NavLink
                to="/dashboard"
                onClick={close}
                data-testid="drawer-nav-dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5 ${isActive ? "bg-white/5" : ""}`
                }
              >
                <div className="w-9 h-9 rounded-md grid place-items-center bg-[#0f0f0f] border border-white/10">
                  <Package className="w-4 h-4 text-white" />
                </div>
                <div className="text-sm text-white">Mis pedidos</div>
                <ChevronRight className="w-4 h-4 text-neutral-600 ml-auto" />
              </NavLink>
              {user.is_reseller && (
                <NavLink
                  to="/revendedores"
                  onClick={close}
                  data-testid="drawer-nav-reseller"
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5 ${isActive ? "bg-white/5" : ""}`
                  }
                >
                  <div className="w-9 h-9 rounded-md grid place-items-center bg-[#0f0f0f] border border-amber-400/40">
                    <Wallet className="w-4 h-4 text-amber-300" />
                  </div>
                  <div className="text-sm text-white">Panel Revendedor</div>
                  <ChevronRight className="w-4 h-4 text-neutral-600 ml-auto" />
                </NavLink>
              )}
              {user.is_admin && (
                <NavLink
                  to="/admin"
                  onClick={close}
                  data-testid="drawer-nav-admin"
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5 ${isActive ? "bg-white/5" : ""}`
                  }
                >
                  <div className="w-9 h-9 rounded-md grid place-items-center bg-[#0f0f0f] border border-[#ff4500]/40">
                    <Shield className="w-4 h-4 text-[#ff4500]" />
                  </div>
                  <div className="text-sm text-white">Panel Admin</div>
                  <ChevronRight className="w-4 h-4 text-neutral-600 ml-auto" />
                </NavLink>
              )}
            </>
          )}

          <div className="text-[10px] uppercase tracking-widest text-neutral-500 px-3 py-2 mt-3">Contacto</div>
          <a
            href="https://wa.me/543821452181?text=Hola!%20Necesito%20ayuda%20con%20una%20recarga"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="drawer-whatsapp-link"
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-white/5"
          >
            <div className="w-9 h-9 rounded-md grid place-items-center bg-emerald-500/10 border border-emerald-500/40">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white leading-tight">Soporte WhatsApp</div>
              <div className="text-[10px] text-neutral-500">+54 3821 452181</div>
            </div>
          </a>
        </nav>

        <div className="border-t border-white/10 p-3">
          {user ? (
            <button
              onClick={async () => {
                await logout();
                close();
                navigate("/");
              }}
              data-testid="drawer-logout-btn"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/40 text-rose-300 text-sm font-semibold"
              style={{ transition: "background 180ms ease" }}
            >
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          ) : (
            <button
              onClick={() => {
                close();
                startGoogleLogin();
              }}
              data-testid="drawer-login-btn"
              className="btn-primary w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm"
            >
              <LogIn className="w-4 h-4" /> Iniciar sesión con Google
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
